import React, { useMemo, useState } from "react";
import {
  FaArrowLeft,
  FaArrowRight,
  FaBookOpen,
  FaCheckCircle,
  FaCreditCard,
  FaCrown,
  FaExclamationCircle,
  FaLock,
  FaMoneyCheckAlt,
  FaPaypal,
  FaRedo,
  FaShieldAlt,
  FaSignOutAlt,
  FaUniversity,
} from "react-icons/fa";

import type {
  CourseDetail,
  CourseLevelKey,
} from "../../MainScreen/Courses/CoursesDetails";

import { purchaseCourse, restoreCourseAccess } from "../services/revenueCatWeb";

import "./Payment.css";

type PaymentMethodKey = "card" | "paypal" | "sepa" | "bank";

type PaymentProps = {
  courseLevel: CourseLevelKey;
  course: CourseDetail;
  userId: string;
  userEmail?: string | null;
  accessError?: string;
  onSubscribed: () => void;
  onLogout?: () => void;
  onBackToCourses?: () => void;
};

type GermanCoursePaymentText = {
  title: string;
  duration: string;
  price: number;
};

const COURSE_PAYMENT_TEXTS: Record<CourseLevelKey, GermanCoursePaymentText> = {
  A1: {
    title: "A1-DEUTSCHKURS",
    duration: "2 Monate",
    price: Number(process.env.REACT_APP_A1_PRICE_EUR ?? 100),
  },
  A2: {
    title: "A2-DEUTSCHKURS",
    duration: "2 Monate",
    price: Number(process.env.REACT_APP_A2_PRICE_EUR ?? 125),
  },
  B1: {
    title: "B1-DEUTSCHKURS",
    duration: "3 Monate",
    price: Number(process.env.REACT_APP_B1_PRICE_EUR ?? 150),
  },
  B2: {
    title: "B2-DEUTSCHKURS",
    duration: "3 Monate",
    price: Number(process.env.REACT_APP_B2_PRICE_EUR ?? 175),
  },
};

const PAYMENT_METHODS: Array<{
  key: PaymentMethodKey;
  title: string;
  subtitle: string;
  badge?: string;
  icon: React.ReactNode;
}> = [
  {
    key: "card",
    title: "Karte",
    subtitle: "Kartenzahlung",
    badge: "Meist genutzt",
    icon: <FaCreditCard />,
  },
  {
    key: "paypal",
    title: "PayPal",
    subtitle: "Schnell bezahlen",
    icon: <FaPaypal />,
  },
  {
    key: "sepa",
    title: "SEPA",
    subtitle: "Lastschrift",
    icon: <FaUniversity />,
  },
  {
    key: "bank",
    title: "Überweisung",
    subtitle: "Bankzahlung",
    icon: <FaMoneyCheckAlt />,
  },
];

const cleanPaymentError = (error: unknown): string => {
  const rawMessage =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : "";

  const message = rawMessage.trim();
  const lowerMessage = message.toLowerCase();

  if (!message) {
    return "";
  }

  if (
    lowerMessage.includes("cancel") ||
    lowerMessage.includes("cancelled") ||
    lowerMessage.includes("canceled") ||
    lowerMessage.includes("abgebrochen")
  ) {
    return "Die Zahlung wurde abgebrochen.";
  }

  if (
    lowerMessage.includes("invalid api key") ||
    lowerMessage.includes("web billing api key") ||
    lowerMessage.includes("revenuecat") ||
    lowerMessage.includes("stripe") ||
    lowerMessage.includes("payment")
  ) {
    return "Die Zahlung konnte nicht gestartet werden. Bitte prüfen Sie die Zahlungseinstellungen.";
  }

  return "Die Zahlung konnte nicht gestartet werden. Bitte versuchen Sie es erneut.";
};

const Payment: React.FC<PaymentProps> = ({
  courseLevel,
  course,
  userId,
  userEmail,
  accessError,
  onSubscribed,
  onLogout,
  onBackToCourses,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [restoreLoading, setRestoreLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<PaymentMethodKey>("card");

  const paymentText = COURSE_PAYMENT_TEXTS[courseLevel];

  const formattedPrice = useMemo(() => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(paymentText.price);
  }, [paymentText.price]);

  const selectedPaymentTitle = useMemo(() => {
    return (
      PAYMENT_METHODS.find((method) => method.key === selectedPaymentMethod)
        ?.title ?? "Karte"
    );
  }, [selectedPaymentMethod]);

  const visibleError =
    errorMessage || (accessError ? cleanPaymentError(accessError) : "");

  const handlePurchase = async (): Promise<void> => {
    setErrorMessage("");

    if (!userId) {
      setErrorMessage("Bitte loggen Sie sich zuerst ein.");
      return;
    }

    setLoading(true);

    try {
      const success = await purchaseCourse({
        appUserId: userId,
        courseLevel,
        email: userEmail,
      });

      if (!success) {
        setErrorMessage(
          "Die Zahlung wurde abgeschlossen, aber der Kurszugang wurde noch nicht erkannt. Bitte klicken Sie auf „Zugang prüfen“.",
        );
        return;
      }

      onSubscribed();
    } catch (error) {
      setErrorMessage(cleanPaymentError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (): Promise<void> => {
    setErrorMessage("");

    if (!userId) {
      setErrorMessage("Bitte loggen Sie sich zuerst ein.");
      return;
    }

    setRestoreLoading(true);

    try {
      const hasAccess = await restoreCourseAccess(
        userId,
        courseLevel,
        userEmail,
      );

      if (hasAccess) {
        onSubscribed();
        return;
      }

      setErrorMessage(
        "Für dieses Konto wurde noch kein aktiver Kurszugang gefunden.",
      );
    } catch (error) {
      setErrorMessage(cleanPaymentError(error));
    } finally {
      setRestoreLoading(false);
    }
  };

  return (
    <section className="course-payment-wrapper">
      <article className="course-payment-card">
        <div className="course-payment-particles" aria-hidden="true">
          <span />
          <span />
          <span />
          <span />
        </div>

        {onBackToCourses && (
          <button
            type="button"
            className="course-payment-back"
            onClick={onBackToCourses}
            aria-label="Zurück zu den Kursen"
          >
            <FaArrowLeft />
          </button>
        )}

        <div className="course-payment-top-row">
          <div className="course-payment-badge">
            <FaCrown />
            <span>Premium-Zugang</span>
          </div>
        </div>

        <h1 className="course-payment-title">Kurszugang kaufen</h1>

        <p className="course-payment-description">
          Kaufen Sie den Kurszugang sicher über unser geschütztes
          Zahlungssystem.
        </p>

        {visibleError && (
          <div className="course-payment-error">
            <FaExclamationCircle />
            <span>{visibleError}</span>
          </div>
        )}

        <div className="course-payment-benefits">
          <div>
            <FaCheckCircle />
            <span>1 Jahr Kurszugang</span>
          </div>

          <div>
            <FaBookOpen />
            <span>10 PDF-Bücher inklusive</span>
          </div>

          <div>
            <FaShieldAlt />
            <span>Sichere Zahlung</span>
          </div>

          <div>
            <FaLock />
            <span>Premium-App inklusive</span>
          </div>
        </div>

        <div className="course-payment-method-box">
          <div className="course-payment-method-heading">
            <span>Zahlungsmethode</span>
            <strong>Standard: Karte</strong>
          </div>

          <div className="course-payment-method-grid">
            {PAYMENT_METHODS.map((method) => (
              <button
                key={method.key}
                type="button"
                className={`course-payment-method ${
                  selectedPaymentMethod === method.key
                    ? "course-payment-method-active"
                    : ""
                }`}
                onClick={() => setSelectedPaymentMethod(method.key)}
              >
                <span className="course-payment-method-icon">
                  {method.icon}
                </span>

                <span className="course-payment-method-text">
                  <strong>{method.title}</strong>
                  <small>{method.subtitle}</small>
                </span>

                {method.badge && (
                  <em className="course-payment-method-badge">
                    {method.badge}
                  </em>
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="course-payment-summary">
          <div>
            <span>Kurs</span>
            <strong>{paymentText.title || course.title}</strong>
          </div>

          <div>
            <span>Dauer</span>
            <strong>{paymentText.duration}</strong>
          </div>

          <div>
            <span>Preis</span>
            <strong>{formattedPrice}</strong>
          </div>

          <div>
            <span>Konto</span>
            <strong>{userEmail || "Eingeloggt"}</strong>
          </div>
        </div>

        <div className="course-payment-actions">
          <button
            type="button"
            className="course-payment-main-button"
            onClick={handlePurchase}
            disabled={loading || restoreLoading}
          >
            <span>
              {loading
                ? "Zahlung wird geöffnet..."
                : `Mit ${selectedPaymentTitle} bezahlen`}
            </span>

            {!loading && <FaArrowRight />}
          </button>

          <button
            type="button"
            className="course-payment-restore-button"
            onClick={handleRestore}
            disabled={loading || restoreLoading}
          >
            <span>
              {restoreLoading ? "Zugang wird geprüft..." : "Zugang prüfen"}
            </span>

            {!restoreLoading && <FaRedo />}
          </button>

          {onLogout && (
            <button
              type="button"
              className="course-payment-logout-button"
              onClick={onLogout}
              disabled={loading || restoreLoading}
            >
              <FaSignOutAlt />
              <span>Abmelden</span>
            </button>
          )}
        </div>

        <p className="course-payment-note">
          Nach erfolgreicher Zahlung wird der Kurs automatisch freigeschaltet.
          Die endgültigen Zahlungsarten werden sicher im Zahlungsfenster
          angezeigt.
        </p>
      </article> 
    </section>
  );
};

export default Payment;